import  React, { useEffect } from 'react';
import dayjs from 'dayjs'; 
import { Alert, Button, Card, Layout, Space, Table } from 'antd';
import { Routes } from '../../../routes'; 
import { EditOutlined, PlusOutlined} from '@ant-design/icons';
import { routeHistory } from '@nackle/biw-web-router';
import { authService } from '../../../auth/authService';
import Spinner from '../../components/Spinner/Spinner';
import useHasError from '../../hooks/useHasError'; 
import useIsLoading from '../../hooks/useIsLoading'; 
import useSearchFilter from '../../hooks/useSearchFilter'; 
import StrongTag from '../../components/StrongTag/StrongTag';
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import DeleteButton from '../../components/DeleteButton/DeleteButton';
import SearchBar from '../../components/SearchBar/SearchBar';
import { useURLQueries } from '../../hooks/useURLQueries'; 
import { renderWithSearchWords } from '../../components/SearchBar/SearchBar';
import useComponentListSets from '../../gql/hooks/useComponentListSets';
import useComponentListSetDelete from '../../gql/hooks/useComponentListSetDelete';
import { getFormattedDateTime } from '../../utils';
import './styles.less';

const ComponentsListPage = ( props ) => {
    const { loadCLS, clsData, clsError, clsLoading  } = useComponentListSets();
    const { deleteComponentList } = useComponentListSetDelete();
    const { currentPage, setCurrentPage, searchText, setSearchText, searchWords, 
        sortField, setSortField, sortDirection, setSortDirection } = useURLQueries();
    const { filter, filteredData } = useSearchFilter();
    const { isLoading } = useIsLoading( { items: [ clsLoading ] } );
    const { hasError } = useHasError( { items: [ clsError ] } );

    const breadcrumbs = [
        { name: 'Course Studio', path: Routes.Home.buildPath() },
        { name: authService.isHoya()  ? 'My Journey Setup' : 'Homepage Setup' }
    ];
 
    useEffect( () => {
        if ( !isLoading && !hasError && clsData && searchText ) {
            filter( searchText, clsData );
        }
    }, [ isLoading, hasError, clsData ] );
 
    
    const handleDeleteClick = async ( item ) => {
        await deleteComponentList( item.componentListSetId  );
        loadCLS();
    };
    
    const handleEditClick = async ( item ) => { 
        routeHistory.push( Routes.ComponentsListEdit.buildPath( item.componentListSetId ) );  
    };

    const handleSearchClick = ( value ) => {
        setCurrentPage( 1 );
        setSortField( 'name' );
        setSortDirection( 'ascend' );
        setSearchText( value );
        filter( value, clsData );
    };

    const handleTableChange = ( pagination, filters, sorter ) => {
        setSortField( sorter?.columnKey );
        setSortDirection( sorter?.order );
    };

    const handlePaginatorChange = ( pagination ) => {
        setCurrentPage( pagination );
    };

    const columns =  [ 
        {
            key: 'name',
            dataIndex: 'name',
            title: 'Name',
            sortOrder: sortField === 'name' && sortDirection,   
            sorter: ( a, b ) => a.name.localeCompare( b.name ),
            render: ( name ) => searchText ? renderWithSearchWords( name, searchWords ) : name
        },
        {
            dataIndex: 'isPublished',
            key: 'isPublished', 
            title: 'Published',
            render: ( isPublished ) => isPublished ? <StrongTag color="green" strontText="Published"/> : ''
        },
        {
            key: 'createdAt',
            dataIndex: 'createdAt',
            title: 'Created At',
            sortOrder: sortField === 'createdAt' && sortDirection,   
            sorter: ( a, b ) => {
                const diff = dayjs( a.createdAt ).diff( dayjs( b.createdAt ) );
                return diff < 0 ? -1 : diff > 0 ? 1 : 0;
            },
            render: ( i ) => getFormattedDateTime( i )
        },
        {
            key: 'updatedAt',
            dataIndex: 'updatedAt',
            title: 'Updated At',
            sortOrder: sortField === 'updatedAt' && sortDirection,   
            sorter: ( a, b ) => {
                const diff = dayjs( a.updatedAt ).diff( dayjs( b.updatedAt ) );
                return diff < 0 ? -1 : diff > 0 ? 1 : 0;
            },
            render: ( i ) => getFormattedDateTime( i )
        },
        {
            key: 'actions', 
            title: 'Actions',
            fixed: 'right', 
            width: '100px',
            render: ( item ) => 
                <>
                    <Space>
                        <Button 
                            type="link" 
                            icon={ <EditOutlined /> } 
                            onClick= { () => handleEditClick( item ) }
                        />
                                
                        <DeleteButton
                            id={ item.id }   
                            title={ 'Confirm Delete' }
                            deleteCallback={ () => handleDeleteClick( item ) }
                            confirmMessage= { 
                                <>
                                    <p>Are you <i>sure</i> you want to permanently delete the component list <b>{ item.name }</b>? 
                                    This action cannot be undone.</p>
                                </>
                            }
                        />
                    </Space>
                </> 
        } 
    ];

    /*************************************
     *  ANCHOR Render
     *************************************/
   
    return ( 
        <Layout className="component-lists-page">
            
            { /* Page header breadcrumbs */ }
            <Breadcrumbs crumbs={ breadcrumbs }/>

            { /* Loading Spinner */ }
            { isLoading &&
                <Spinner/>
            }
             
            { /* Main Content */ }
            { !isLoading &&
                <Card
                    className="component-lists-content"
                    title={ authService.isHoya() ? 'My Journey Setup' : 'Homepage Setup' }
                    extra={ !hasError &&
                        <Button
                            icon={ <PlusOutlined /> }
                            onClick={ () => routeHistory.push( Routes.ComponentsListCreate.buildPath() ) }
                            type="text"
                        >
                            { authService.isHoya() ? 'Create New My Journey Page' : 'Create New Homepage' }
                        </Button>
                    }
                >   
                    { /* Error message */ }
                    { hasError && 
                        <Alert 
                            type="error"
                            message="Error"
                            description="An unknown error occurred. Please try again later."
                        />
                    }

                    { !hasError &&
                        <>
                            { /* Search bar */ }
                            <SearchBar
                                searchText={ searchText }
                                placeHolerText="Search by name"
                                handleSearchCallback={ handleSearchClick }
                            /> 

                            { /* Data table */ }
                            <Table 
                                rowKey='id'
                                columns={ columns }
                                dataSource={ filteredData || clsData }
                                onChange={ handleTableChange }
                                pagination={ {
                                    onChange: handlePaginatorChange,
                                    defaultCurrent: currentPage || 1,
                                    showQuickJumper: false,
                                    showSizeChanger: false
                                } }
                            />
 
                        </>
                    }
                </Card>
            }
        </Layout>
    );
};

export default ComponentsListPage;
